import { computed, Injectable, signal } from '@angular/core';
import {
	addDoc,
	collection,
	collectionGroup,
	CollectionReference,
	collectionSnapshots,
	deleteDoc,
	doc,
	DocumentData,
	DocumentReference,
	Firestore,
	FirestoreError,
	getCountFromServer,
	getDoc,
	getDocs,
	limit,
	orderBy,
	Query,
	query,
	setDoc,
	updateDoc,
	where,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GoogleIdentityService, MAS_Programs_Service } from '.';
import { MasAccountConverterService } from '../converters/mas-accounts.converter';
import { MasAccountAuditService } from './mas-account-audit.service';
import { chunkForFirestoreIn } from '@common/domain/firestore-in-query';
import { Account, AccountAttendance, AttendanceViolation, MASAccountModel, MASParentAccountModel, TournamentStanding } from '../models';
import { AdvancedDebugging, DateTimeUtils, EmailMessage, SendEmail } from '../utilities';

/** Options for `MASAccountModel.save` audit trail (`mas-accounts-history`). */
export type AccountSaveAuditOptions = {
	history: Record<string, any>;
	autofix?: Record<string, any>;
	userId: string;
	caller: string;
};

@Injectable({
	providedIn: 'root',
})
export class MAS_Accounts_Service {
	private _myAccountModel = signal<MASParentAccountModel | null>(null);
	private _includeYAS = signal(false);
	private advancedDebugging = new AdvancedDebugging();
	public readonly myAccountModel = computed(() => this._myAccountModel());
	public readonly includeYAS = computed(() => this._includeYAS());

	public clearSessionState(): void {
		this._myAccountModel.set(null);
		this._includeYAS.set(false);
	}

	constructor(
		public firestore: Firestore,
		private identityService: GoogleIdentityService,
		private programsService: MAS_Programs_Service,
		public masAccountConverter: MasAccountConverterService,
		private accountAudit: MasAccountAuditService,
	) {}

	public get firestoreInstance(): Firestore {
		return this.firestore;
	}

	public get converter(): MasAccountConverterService {
		return this.masAccountConverter;
	}

	/** Signed-in user id for `mas-accounts-history` `changedBy`. */
	auditUserId(): string {
		return this.identityService.isSignedIn ?? 'Strange: There should always be a user!';
	}

	diffAccountSnapshots(before: Account, after: Account): Record<string, { from: any; to: any }> {
		return this.masAccountConverter.fromRaw(before).diffBetweenSnapshots(before, after);
	}

	private reportAccountsError(operation: string, error: unknown, context?: Record<string, unknown>): void {
		this.advancedDebugging.captureError(operation, error, {
			tags: { operation },
			...context,
		});
	}

	private failAccounts(operation: string, error: unknown, context?: Record<string, unknown>): never {
		this.reportAccountsError(operation, error, context);
		throw error;
	}

	public async fetchAccountData(userId: string) {
		try {
			const docRef = doc(this.firestore, 'accounts', userId);
			const docSnap = await getDoc(docRef);

			if (docSnap.exists()) {
				const raw = docSnap.data() as Account; // 👈 cast here
				const model = this.masAccountConverter.fromRaw(raw);
				this._myAccountModel.set(new MASParentAccountModel(model));
			}
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.fetchAccountData', error, { userId });
		}
	}

	async createAccount(payload: Account): Promise<DocumentReference<DocumentData>> {
		try {
			const ref = collection(this.firestore, 'mas-accounts');
			return await addDoc(ref, payload);
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.createAccount', error);
		}
	}

	async deleteAccountAttendance(memberId: string, documentId: string): Promise<void> {
		try {
			const ref = doc(this.firestore, `mas-accounts/${memberId}/mas-accounts-attendance`, documentId);
			await deleteDoc(ref);
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.deleteAccountAttendance', error, { memberId, documentId });
		}
	}

	/**
	 * ! @deprecated Use getAccountsAsModels instead.
	 */
	async getAccounts(): Promise<Account[]> {
		try {
			const q = query(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>);
			const snapshot = await getDocs(q);
			return snapshot.docs.map(m => ({ ...m.data(), id: m.id }));
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.getAccounts', error);
		}
	}

	async getAccountsAsModels(): Promise<MASAccountModel[]> {
		try {
			const q = query(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>);
			const snapshot = await getDocs(q);

			return snapshot.docs.map(doc =>
				this.masAccountConverter.fromRaw({
					...doc.data(),
					id: doc.id,
				}),
			);
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.getAccountsAsModels', error);
		}
	}

	async archiveCollection(path: string): Promise<void> {
		try {
			const q = query(collection(this.firestore, path) as CollectionReference<any>);
			const snapshot = await getDocs(q);

			const archivePromises = snapshot.docs.map(async docSnapshot => {
				const ref = doc(this.firestore, `${path}-archive`, docSnapshot.id);
				await setDoc(ref, docSnapshot.data());
			});

			await Promise.all(archivePromises);
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.archiveCollection', error, { path });
			throw new Error(`Failed to archive collection at path: ${path}`);
		}
	}

	async getAccountAttendanceById(accountId: string, scheduleId: string): Promise<Boolean> {
		const docRef = doc(this.firestore, `mas-accounts/${accountId}/mas-accounts-attendance/`, scheduleId);
		const docSnap = await getDoc(docRef);
		return docSnap.exists();
	}

	async getAccountAttendance(id: string, programs: string[]): Promise<AccountAttendance[]> {
		this.advancedDebugging.log('programs', programs);
		if (!programs?.length) return [];

		const scheduleNames = [...new Set([...programs, 'Blackbelt'])];
		const coll = collection(this.firestore, `mas-accounts/${id}/mas-accounts-attendance/`) as CollectionReference<AccountAttendance>;
		const merged: AccountAttendance[] = [];

		for (const batch of chunkForFirestoreIn(scheduleNames)) {
			const q = query<AccountAttendance, DocumentData>(coll, where('scheduleName', 'in', batch));
			const qDocs = await getDocs(q);
			if (qDocs) {
				merged.push(...qDocs.docs.map(m => m.data()));
			}
		}

		return merged;
	}

	async getAccountViolations(id: string, cycleName?: string): Promise<AttendanceViolation[]> {
		let q: Query<AttendanceViolation>;

		if (cycleName) {
			q = query<AttendanceViolation, DocumentData>(
				collection(this.firestore, `mas-accounts/${id}/mas-accounts-violation/`) as CollectionReference<AttendanceViolation>,
				where('cycleName', '==', cycleName),
			);
		} else {
			q = query<AttendanceViolation, DocumentData>(collection(this.firestore, `mas-accounts/${id}/mas-accounts-violation/`) as CollectionReference<AttendanceViolation>);
		}

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => {
			const data = m.data();
			data.id = m.id;
			return data;
		});
	}

	async getAccountAttendanceCount(id: string, programs: string[], cycle: string) {
		if (!programs?.length) return [];

		const scheduleNames = [...new Set(programs)];
		const coll = collection(this.firestore, `mas-accounts/${id}/mas-accounts-attendance/`) as CollectionReference<AccountAttendance>;
		const merged: AccountAttendance[] = [];

		for (const batch of chunkForFirestoreIn(scheduleNames)) {
			const q = query<AccountAttendance, DocumentData>(
				coll,
				where('scheduleName', 'in', batch),
				where('cycleName', '==', cycle),
			);
			const qDocs = await getDocs(q);
			if (qDocs) {
				merged.push(...qDocs.docs.map(m => m.data()));
			}
		}

		return merged;
	}

	async getAccountStandings(id: string): Promise<TournamentStanding[]> {
		try {
			const ref = collection(this.firestore, `mas-accounts/${id}/mas-accounts-standings`) as CollectionReference<TournamentStanding>;
			const qSnap = await getDocs(ref);

			const standings = qSnap.docs.map(doc => doc.data());

			return standings;
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.getAccountStandings', error, { accountId: id });
			return [];
		}
	}

	getAccountAttendanceByProgram(accountId: string, program: string): Promise<number> {
		const q = query(collection(this.firestore, `mas-accounts/${accountId}/mas-accounts-attendance/`), where('scheduleName', '==', program));
		return getDocs(q).then(data => {
			return data.size;
		});
	}

	getAccountAttendanceTotal(accountId: string): Promise<number> {
		const col = collection(this.firestore, `mas-accounts/${accountId}/mas-accounts-attendance`);
		const q = query(col);
		return getCountFromServer(q).then(snapshot => snapshot.data().count);
	}

	async prepareMyAccount(email: string): Promise<boolean> {
		try {
			const myAccountSignal = new MASParentAccountModel(undefined as any);

			myAccountSignal.parent = await this.prepareParent(email);

			myAccountSignal.parent.setStandings(await this.getAccountStandings(myAccountSignal.parent.id));

			myAccountSignal.parent.children = await this.prepareChildren(myAccountSignal.parent);

			if (myAccountSignal.parent.children?.length) {
				await Promise.all(
					myAccountSignal.parent.children.map(async child => {
						const standings = await this.getAccountStandings(child.id);
						child.setStandings(standings);
					}),
				);
			}

			this._myAccountModel.set(myAccountSignal);

			try {
				const YAS = await this.programsService.getProgramByName('YAS');
				const yasMembership = YAS?.memberships.map(m => m.memberId) ?? [];
				const accountIds = myAccountSignal.fullFamily.map(m => m.raw.id);
				this._includeYAS.set(accountIds.some(id => yasMembership.includes(id)));
			} catch (yasErr) {
				this.reportAccountsError('mas:MAS_Accounts_Service.prepareMyAccount.includeYAS', yasErr);
				this._includeYAS.set(false);
			}

			return true;
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.prepareMyAccount', error);
			return false;
		}
	}

	async prepareParent(email: string): Promise<MASAccountModel> {
		try {
			// Normalize
			email = email?.trim().toLowerCase();

			const q = query<Account, DocumentData>(
				collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
				where('emailAddresses.value', '==', email),
				where('mas.nodeType', '==', 'parent'),
			);

			const snap = await getDocs(q);

			if (window.location.hostname === 'localhost') {
				this.advancedDebugging.log('mas:MAS_Accounts_Service.prepareParent', { matchCount: snap.size });
			}

			const account = snap.docs[0]?.data();

			if (!account) {
				if (window.location.hostname === 'localhost') {
					this.advancedDebugging.log('mas:MAS_Accounts_Service.prepareParent', { noAccountFound: true });
				}

				this.identityService.signOut();
				window.alert('Invalid MAS Email!');
				throw new Error(`No parent account found for ${email}`);
			}

			const parentModel = this.masAccountConverter.fromRaw(account);

			return parentModel;
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.prepareParent', error);
			throw error;
		}
	}
	async prepareChildren(parent: MASAccountModel): Promise<MASAccountModel[]> {
		try {
			const linkedAccountIds = parent.raw.mas.linkedAccounts?.map(m => m.accountId) ?? [];

			if (linkedAccountIds.length === 0) return [];

			const linkedAccountsRaw = await this.getAccountsById(linkedAccountIds);
			const linkedAccountModels = linkedAccountsRaw.map(raw => this.masAccountConverter.fromRaw(raw));

			return linkedAccountModels;
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.prepareChildren', error, {
				parentId: parent.id,
				linkedCount: parent.raw.mas.linkedAccounts?.length ?? 0,
			});
			return [];
		}
	}

	/* 	async getLinkedAccountsByParent(parent: MASAccountModel): Promise<boolean> {
		try {
			// Fetch and convert linked accounts
			const linkedAccountIds = parent.raw.mas.linkedAccounts?.map(m => m.accountId) ?? [];
			const linkedAccounts: MASAccountModel[] = linkedAccountIds.length
				? await this.getAccountsById(linkedAccountIds).then(accounts => accounts.map(raw => this.masAccountConverter.fromRaw(raw)))
				: [];

			this._linkedAccounts.set(linkedAccounts);

			// Merge memberships
			const allMemberships = new Set<string>();
			linkedAccounts.forEach(child => {
				child.raw.memberships?.forEach(m => allMemberships.add(m));
			});
			if (parent.raw.mas?.accountSettings?.member === 'true') {
				parent.raw.memberships?.forEach(m => allMemberships.add(m));
			}
			const myProgramsListArray = Array.from(allMemberships);
			this._myProgramsList.set(myProgramsListArray);

			// YAS check
			const YAS = await this.programsService.getProgramByName('YAS');
			const yasMembership = YAS?.memberships.map(m => m.memberId) ?? [];
			this.advancedDebugging.log('getAccountsByEmail=>yasMembership', yasMembership);

			const accountIds = [...linkedAccountIds, parent.raw.id];
			const includeYAS = accountIds.some(id => yasMembership.includes(id));
			this._includeYAS.set(includeYAS);
			this.advancedDebugging.log('getAccountsByEmail=>includeYAS', includeYAS);

			return true;
		} catch (error) {
			this.advancedDebugging.captureError('MAS_Accounts_Service=>', error);
			throw error;
			return false;
		}
	} */

	/*
 ! async getAccountByEmail(email: string): Promise<MASAccountModel[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('emailAddresses.value', '==', email),
			where('mas.accountSettings.billing', '==', 'true'),
			limit(1)
		);

		const snapshot = await getDocs(q);
		const accountModels: MASAccountModel[] = snapshot.docs.map(doc => {
			const raw: Account = { ...doc.data(), id: doc.id };
			return this.masAccountConverter.fromRaw(raw);
		});

		if (accountModels.length > 0) {
			this._myAccount.set(accountModels[0]); // Set the signal with the model
			this.advancedDebugging.log('getAccountByEmail=>myAccount', accountModels[0]);
		}

		return accountModels;
	} */

	async getAccountById(id: string): Promise<Account> {
		const docRef = doc(this.firestore, 'mas-accounts', id);
		const docSnap = await getDoc(docRef);

		if (!docSnap.exists()) {
			throw new Error(`Account not found for ID: ${id}`);
		}

		return docSnap.data() as Account;
	}

	async getAccountModelById(id: string): Promise<MASAccountModel> {
		try {
			const docRef = doc(this.firestore, 'mas-accounts', id) as DocumentReference<Account>;
			const docSnap = await getDoc(docRef);

			if (!docSnap.exists()) {
				throw new Error(`Account not found for ID: ${id}`);
			}

			const raw = docSnap.data() as Account;

			// Convert raw Firestore data into MASAccountModel,
			// making sure to include the Firestore document id.
			return this.masAccountConverter.fromRaw({
				...raw,
				id: docSnap.id,
			});
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.getAccountModelById', error, { accountId: id });
		}
	}

	async getAccountsByLinked(links: string[]) {
		if (!links?.length) return [];

		try {
			return await this.getAccountsById(links);
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.getAccountsByLinked', error, { linkCount: links.length });
			return [];
		}
	}

	getAccountTrivaData(): Observable<Account[]> {
		return collectionSnapshots<Account>(
			query<Account, DocumentData>(
				collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
				where('mas.triviaQuestionData.cumulativeCorrect', '>', 0),
				where('mas.accountSettings.status', 'in', ['Active', 'Pending', 'Trial']),
				orderBy('mas.triviaQuestionData.cumulativeCorrect'),
			),
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			}),
		);
	}

	async getAccountsByActiveBillingStatus(): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.status', '!=', 'Inactive'),
			where('mas.accountSettings.billing', '==', 'true'),
			orderBy('names.givenName'),
		);

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	async getAccountsByBillingStatus(billing: 'true' | 'false' = 'true') {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.billing', '==', billing),
			orderBy('names.givenName'),
		);

		const qDocs = await getDocs(q);
		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	/**
	 * @deprecated Use getAccountModelsByActiveMember instead.
	 */
	getAccountsByActiveMember(): Observable<Account[]> {
		return collectionSnapshots<Account>(
			query<Account, DocumentData>(
				collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
				where('mas.accountSettings.status', '!=', 'Inactive'),
				where('mas.accountSettings.member', '==', 'true'),
				orderBy('names.givenName'),
			),
		).pipe(
			map(accountChanges => {
				return accountChanges.map(accounts => {
					const account = accounts.data() as Account;
					account.id = accounts.id;
					return account;
				});
			}),
		);
	}

	getAccountModelsByActiveMember(): Observable<MASAccountModel[]> {
		return collectionSnapshots<Account>(
			query(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('mas.accountSettings.member', '==', 'true'), orderBy('names.givenName')),
		).pipe(
			map(accountChanges =>
				accountChanges
					.map(snapshot => {
						const raw = snapshot.data();
						raw.id = snapshot.id;
						return this.masAccountConverter.fromRaw(raw);
					})
					.filter(model => model.raw.mas.accountSettings.status !== 'Inactive'),
			),
		);
	}

	async getAccountsByMemberAsPromise(): Promise<Account[]> {
		const q = query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('mas.accountSettings.member', '==', 'true'));

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	getAccountsByActiveParent(): Observable<Account[]> {
		return collectionSnapshots<Account>(
			query<Account, DocumentData>(
				collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
				where('mas.accountSettings.status', '!=', 'Inactive'),
				where('mas.nodeType', '==', 'parent'),
			),
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			}),
		);
	}

	/**
	 *! @deprecated USE getAccountModelsByActiveStatus() INSTEAD
	 */
	getAccountsByActiveStatus(): Observable<Account[]> {
		return collectionSnapshots<Account>(
			query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('mas.accountSettings.status', '!=', 'Inactive')),
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			}),
		);
	}
	getAccountModelsByActiveStatus(): Observable<MASAccountModel[]> {
		return collectionSnapshots<Account>(
			query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('mas.accountSettings.status', '!=', 'Inactive')),
		).pipe(
			map(changes =>
				changes.map(snapshot => {
					const raw: Account = { ...snapshot.data(), id: snapshot.id };
					return this.masAccountConverter.fromRaw(raw);
				}),
			),
		);
	}

	async getAccountsById(idList: string[]): Promise<Account[]> {
		const ids = [...new Set(idList.filter(Boolean))];
		if (!ids.length) return [];

		const coll = collection(this.firestore, 'mas-accounts') as CollectionReference<Account>;
		const merged: Account[] = [];

		for (const batch of chunkForFirestoreIn(ids)) {
			const q = query<Account, DocumentData>(coll, where('id', 'in', batch));
			const qDocs = await getDocs(q);
			if (qDocs) {
				merged.push(...qDocs.docs.map(m => m.data()));
			}
		}

		return merged;
	}

	getAccountsByInactiveStatus(): Observable<Account[]> {
		return collectionSnapshots<Account>(
			query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('mas.accountSettings.status', '==', 'Inactive')),
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			}),
		);
	}

	async getMassEmailListAsModels(): Promise<MASAccountModel[]> {
		try {
			const q = query(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>);
			const snapshot = await getDocs(q);

			return snapshot.docs.map(doc =>
				this.masAccountConverter.fromRaw({
					...doc.data(),
					id: doc.id,
				}),
			);
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.getMassEmailListAsModels', error);
		}
	}

	async getTrialAccountsAsModels(): Promise<MASAccountModel[]> {
		try {
			const q = query(
				collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
				where('mas.accountSettings.status', '==', 'Trial'),
				orderBy('mas.accountSettings.trialEndDate'),
			);
			const snapshot = await getDocs(q);

			return snapshot.docs.map(doc =>
				this.masAccountConverter.fromRaw({
					...doc.data(),
					id: doc.id,
				}),
			);
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.getTrialAccountsAsModels', error);
		}
	}

	/**
	 * ! @deprecated Use newProperty instead.
	 * ONE TIME USE TO CLEANUP DATA, REMOVE AFTER DATA IS CLEAN
	 */
	async getInactiveChildrenWithParent(): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.status', '==', 'Inactive'),
			where('mas.nodeType', '==', 'child'),
		);

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	async getMembersDisenrolled(): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.status', '==', 'Inactive'),
			where('mas.accountSettings.trialConversion', '==', true),
			where('mas.accountSettings.member', '==', 'true'),
		);

		const qDocs = await getDocs(q);
		if (!qDocs) return [];
		return qDocs.docs.map(m => m.data());
	}

	async getTrialConversions(): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.member', '==', 'true'),
			where('mas.accountSettings.status', '!=', 'Trial'),
			where('mas.accountSettings.trialEndDate', '>=', '2025-01-01'),
		);

		const qDocs = await getDocs(q);
		if (!qDocs) return [];
		return qDocs.docs.map(m => m.data());
	}

	async getMembersEnrolled(): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.member', '==', 'true'),
			where('mas.accountSettings.trialConversion', '==', true),
			where('mas.accountSettings.activeDate', '!=', null),
		);

		const qDocs = await getDocs(q);
		if (!qDocs) return [];
		return qDocs.docs.map(m => m.data());
	}

	async getAccountsByLinkedProperties(searchProperties: any): Promise<Account[]> {
		const q = query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('mas.linkedAccounts', 'array-contains', searchProperties));

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	async getAccountsByProgram(programId: string): Promise<Account[]> {
		const q = query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('memberships', 'array-contains', programId));

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	async getAccountModelsByProgram(programId: string): Promise<MASAccountModel[]> {
		const q = query<Account, DocumentData>(collection(this.firestore, 'mas-accounts') as CollectionReference<Account>, where('memberships', 'array-contains', programId));

		const qDocs = await getDocs(q);

		if (!qDocs || qDocs.empty) return [];

		return qDocs.docs.map(doc => this.masAccountConverter.fromRaw(doc.data()));
	}

	async getAccountsBySchool(school: string): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.school', '==', school),
			where('mas.accountSettings.status', '!=', 'Inactive'),
			where('mas.accountSettings.member', '==', 'true'),
			orderBy('names.givenName'),
		);

		const qDocs = await getDocs(q);
		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	async getAccountsWithSchools(): Promise<Account[]> {
		const q = query<Account, DocumentData>(
			collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
			where('mas.accountSettings.school', '>', ''),
			where('mas.accountSettings.status', '!=', 'Inactive'),
			where('mas.accountSettings.member', '==', 'true'),
			orderBy('mas.accountSettings.school'),
		);

		const qDocs = await getDocs(q);

		if (!qDocs) return [];

		return qDocs.docs.map(m => m.data());
	}

	async getActiveAccountForInspect(): Promise<MASAccountModel[]> {
		try {
			const accountsCollection = collection(this.firestore, 'mas-accounts') as CollectionReference<Account>;
			const accountsQuery = query<Account, DocumentData>(accountsCollection, where('mas.accountSettings.status', '!=', 'Inactive'));
			const querySnapshot = await getDocs(accountsQuery);

			return querySnapshot.docs.map(doc =>
				this.masAccountConverter.fromRaw({
					...doc.data(),
					id: doc.id,
				}),
			);
		} catch (error) {
			const firestoreError = error as FirestoreError;
			this.reportAccountsError('mas:MAS_Accounts_Service.getActiveAccountForInspect', error);
			throw new Error('Failed to fetch accounts: ' + firestoreError.message);
		}
	}

	async getBillingEmail(id: string): Promise<any> {
		const docRef = doc(this.firestore, 'mas-accounts', id);
		const docSnap = await getDoc(docRef);

		const billing = docSnap.data() as Account;
		if (billing.emailAddresses?.value) {
			return billing.emailAddresses?.value;
		} else {
			return null;
		}
	}

	async getReservedEmails(childArray: string[]): Promise<any> {
		const childBatches = [];
		const childDocs: Account[] = [];
		const childDocsSnaps: DocumentData[] = [];

		const parentArray: string[] = [];
		const parentBatches = [];
		const parentDocsSnaps: DocumentData[] = [];
		const parentDocs: Account[] = [];

		const emailArray: string[] = [];

		while (childArray.length) {
			const batch = childArray.splice(0, 10);
			childBatches.push(query(collection(this.firestore, 'mas-accounts'), where('id', 'in', [...batch])));
		}

		for (let i = 0; i < childBatches.length; i++) {
			childDocsSnaps.push(getDocs(childBatches[i]));

			await getDocs(childBatches[i])
				.then(results => results.docs)
				.then(docs => {
					docs.forEach(doc => {
						const data = doc.data() as Account;
						childDocs.push(data);
					});
				});
		}

		childDocs.forEach(doc => {
			const parent = doc.mas.linkedAccounts
				?.filter(f => f.type === 'parent')
				.map(m => m)
				.pop()?.accountId;

			if (doc.emailAddresses?.value) emailArray.push(doc.emailAddresses.value);
			if (parent) parentArray.push(parent);
		});

		while (parentArray.length) {
			const batch = parentArray.splice(0, 10);
			parentBatches.push(query(collection(this.firestore, 'mas-accounts'), where('id', 'in', [...batch])));
		}

		for (let i = 0; i < parentBatches.length; i++) {
			parentDocsSnaps.push(getDocs(parentBatches[i]));

			await getDocs(parentBatches[i])
				.then(results => results.docs)
				.then(docs => {
					docs.forEach(doc => {
						const data = doc.data() as Account;
						parentDocs.push(data);
					});
				});
		}
		return [].concat(emailArray as [], parentDocs.map(m => m.emailAddresses?.value) as []);
	}

	async handleSMSReminders(option: boolean) {
		return await this.updateReminderSetting('enableSMS', option, 'handleSMSReminders');
	}

	async handleEmailReminders(option: boolean) {
		return await this.updateReminderSetting('enableEmailReminders', option, 'handleEmailReminders');
	}

	private async updateReminderSetting(key: 'enableSMS' | 'enableEmailReminders', value: boolean, callerContext: string) {
		const parentModel = this.myAccountModel()?.parent;
		if (!parentModel) return;

		const before = JSON.stringify(parentModel.raw);

		const updatedRaw = {
			...parentModel.raw,
			mas: {
				...parentModel.raw.mas,
				accountSettings: {
					...parentModel.raw.mas.accountSettings,
					[key]: value,
				},
			},
		};

		const after = JSON.stringify(updatedRaw);
		const diff = parentModel.diffBetweenSnapshots(JSON.parse(before), JSON.parse(after));

		const updatedModel = this.masAccountConverter.fromRaw(updatedRaw);

		// Re-wrap the updated parent in a new MASParentAccountModel and update the signal
		this._myAccountModel.set(new MASParentAccountModel(updatedModel));

		return await this.persistAccount(updatedModel, 'existing', false, {
			history: diff,
			userId: this.identityService.isSignedIn ?? 'Strange: There should always be a user!',
			caller: `MAS_Accounts_Service=>${callerContext}`,
		});
	}

	async setAccount(
		id: string,
		payload: Account,
		mode: 'existing' | 'other' = 'existing',
		audit?: AccountSaveAuditOptions,
	): Promise<Account> {
		this.advancedDebugging.log('setAccount=>payload', payload);
		try {
			if (audit) {
				const model = this.masAccountConverter.fromRaw({ ...payload, id });
				return this.persistAccount(model, mode === 'existing' ? 'existing' : 'new', mode === 'existing', audit);
			}
			const method = mode === 'existing' ? true : false;
			const ref = doc(this.firestore, 'mas-accounts', id);
			await setDoc(ref, payload, { merge: method });
			return payload;
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.setAccount', error, { accountId: id, mode });
		}
	}

	async setAccountAttendance(id: string, payload: any): Promise<void> {
		this.advancedDebugging.log('setAccountAttendance=>payload', payload);

		const ref = doc(this.firestore, `mas-accounts/${id}/mas-accounts-attendance`, payload.scheduleId);
		return await setDoc(ref, payload, { merge: true });
	}

	async setAccountViolation(id: string, payload: any): Promise<void> {
		this.advancedDebugging.log('setAccountViolation=>payload', payload);

		const ref = doc(this.firestore, `mas-accounts/${id}/mas-accounts-violation`, payload.scheduleId);
		return await setDoc(ref, payload, { merge: true });
	}

	/** @deprecated Use {@link appendAccountHistory} via `MASAccountModel.save` / `MasAccountAuditService`. */
	async setAccountHistory(id: string, data: any): Promise<void> {
		this.advancedDebugging.log('setAccountHistory=>payload', data);
		const ref = doc(this.firestore, `mas-accounts/${id}/mas-accounts-history`, data);
		return await setDoc(ref, data, { merge: true });
	}

	async setAccountTrivia(id: string, payload: any): Promise<void> {
		this.advancedDebugging.log('setAccountTrivia=>payload', payload);

		const ref = doc(this.firestore, `mas-accounts/${id}/mas-accounts-trivia`, payload.lastDate);
		return await setDoc(ref, payload, { merge: true }).then(doc => doc);
	}

	async updateAccount(id: string, payload: Partial<Account>, audit?: AccountSaveAuditOptions): Promise<void> {
		try {
			if (audit) {
				const existing = await this.getAccountById(id);
				if (!existing) {
					throw new Error(`Account not found: ${id}`);
				}
				const merged: Account = { ...existing, ...payload, id };
				const model = this.masAccountConverter.fromRaw(merged);
				await this.persistAccount(model, 'existing', true, audit);
				return;
			}
			const ref = doc(this.firestore, 'mas-accounts', id);
			await updateDoc(ref, payload);
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.updateAccount', error, { accountId: id });
		}
	}

	async getAttendanceData(id: string) {
		const q = query(collectionGroup(this.firestore, 'mas-accounts-attendance'), where('scheduleId', '>=', id), limit(500));

		const attendance = (await getDocs(q)).docs.map(m => {
			const data = m.data();
			data['id'] = m.id;

			data['parent'] = m.ref.parent.parent?.id;
			return data;
		});
		return attendance as {
			cycleName: string;
			parent: string;
			scheduleId: string;
			scheduleName: string;
		}[];
	}

	getLastTournament(): Observable<{
		tournamentDate: string;
		tournamentName: string;
	}> {
		// Get real-time snapshots of the 'mas-accounts-tournaments' collection group
		const tournaments$ = collectionSnapshots(collectionGroup(this.firestore, 'mas-accounts-tournaments')).pipe(
			map(snapshot => {
				const tournaments = snapshot.map(m => {
					// Explicitly type the data
					const data = m.data() as { [key: string]: any };
					return {
						tournamentDate: data['tournamentDate'],
						tournamentName: data['tournamentName'],
					};
				});

				// Sort tournaments by date in ascending order
				tournaments.sort((a, b) => new Date(a.tournamentDate).getTime() - new Date(b.tournamentDate).getTime());

				// Return the last tournament or a fallback value
				return tournaments.length > 0 ? tournaments[tournaments.length - 1] : { tournamentDate: 'N/A', tournamentName: 'No Data Available' };
			}),
		);

		return tournaments$;
	}
	async deleteCorruptAttendance(path: string, id: string) {
		const ref = doc(this.firestore, path, id);
		return await deleteDoc(ref);
	}

	async deleteAccountViolation(memberId: string, documentId: string): Promise<void> {
		const ref = doc(this.firestore, `mas-accounts/${memberId}/mas-accounts-violation`, documentId);
		return await deleteDoc(ref).then(doc => doc);
	}

	async getBillingAccount(account: Account) {
		try {
			if (account.mas.accountSettings.billing === 'false') {
				const parent = account.mas.linkedAccounts?.filter(f => f.type === 'parent');
				if (!parent) throw new Error('missing billing account');
				return await this.getAccountById(parent[0].accountId);
			} else {
				return account;
			}
		} catch (error) {
			this.reportAccountsError('mas:MAS_Accounts_Service.getBillingAccount', error, {
				accountId: account.id,
			});
			throw error;
		}
	}

	async sendEmailViaFirestore(email: EmailMessage): Promise<void> {
		if (window.location.hostname == 'localhost') return;
		const sendEmail = new SendEmail(this.firestore);
		sendEmail.writeEmailToFirestore(email);
	}

	/** Persist `mas-accounts` doc + optional audited history (canonical write path). */
	async persistAccount(
		model: MASAccountModel,
		state: 'existing' | 'new',
		merge: boolean = true,
		audit?: AccountSaveAuditOptions,
	): Promise<Account> {
		const sanitizedRaw = this.sanitizeAccountPayload(model.raw);
		const sanitizedHistory = audit?.history ? this.sanitizeAccountPayload(audit.history) : null;
		const sanitizedAutofix = audit?.autofix ? this.sanitizeAccountPayload(audit.autofix) : null;

		try {
			if (state === 'existing' && sanitizedRaw.id) {
				const ref = doc(this.firestore, 'mas-accounts', sanitizedRaw.id);
				await setDoc(ref, sanitizedRaw, { merge });
			} else {
				const newDocRef = doc(collection(this.firestore, 'mas-accounts'));
				sanitizedRaw.id = newDocRef.id;
				await setDoc(newDocRef, sanitizedRaw);
				model.raw.id = sanitizedRaw.id;
			}

			if (audit?.userId && sanitizedHistory && Object.keys(sanitizedHistory).length > 0) {
				const accountId = sanitizedRaw.id || model.id;
				if (accountId) {
					await this.appendAccountHistory(accountId, {
						changedBy: audit.userId,
						caller: audit.caller,
						changes: sanitizedHistory,
						autofix: sanitizedAutofix ?? undefined,
					});
				}
			}

			return sanitizedRaw;
		} catch (error) {
			this.failAccounts('mas:MAS_Accounts_Service.persistAccount', error, {
				accountId: model.id,
				state,
				merge,
			});
		}
	}

	private sanitizeAccountPayload(obj: unknown): any {
		if (Array.isArray(obj)) {
			return obj.map(value => this.sanitizeAccountPayload(value));
		}
		if (obj !== null && typeof obj === 'object') {
			return Object.fromEntries(
				Object.entries(obj).map(([key, value]) => [key, value === undefined ? null : this.sanitizeAccountPayload(value)]),
			);
		}
		return obj === undefined ? null : obj;
	}

	async appendAccountHistory(
		accountId: string,
		entry: { changedBy: string; caller: string; changes: Record<string, any>; autofix?: Record<string, any> },
	): Promise<void> {
		await this.accountAudit.append(this.firestore, accountId, entry);
	}

	async loadAccountHistoryFormatted(accountId: string): Promise<{ key: string; value: Record<string, unknown> }[]> {
		return this.accountAudit.loadFormatted(this.firestore, accountId);
	}
}
